import { languageContext } from '@src/context/language.context'
import type { ReactNode } from 'react'
import { useContext, useMemo } from 'react'
import { Helmet } from 'react-helmet-async'

interface Label {
  name: string
  value: string
}

interface TitleTag {
  tagName: 'title'
  content: string
}

interface MetaTag {
  tagName: 'meta'
  attributes: {
    name?: string
    property?: string
    content: string
  }
}

export interface LinkTag {
  tagName: 'link'
  attributes: {
    rel: string
    hreflang: string
    href: string
  }
}

export type SeoMetaTag = TitleTag | MetaTag | LinkTag

export interface SeoProps {
  tags?: SeoMetaTag[]
  /**
   * Used instead of tags for migrated queries
   */
  _seoMetaTags?: SeoMetaTag[]
  labels?: Label[]
  noIndex?: boolean
  title?: string
}
const defaultTags: SeoMetaTag[] = []
const defaultLabels: Label[] = []

export function Seo({
  tags = defaultTags,
  _seoMetaTags = tags,
  labels = defaultLabels,
  noIndex = false,
  title = '',
}: SeoProps): ReactNode {
  const titleTag = _seoMetaTags?.find(t => t.tagName === 'title')

  const titleText = titleTag?.content || title

  const metaTags = useMemo(() => {
    const filteredMetaTags = (_seoMetaTags || []).filter(
      t => ('tag' in t && t.tag === 'meta') || t.tagName === 'meta',
    )

    labels.slice(0, 2).forEach((label, i) => {
      filteredMetaTags.push({
        tagName: 'meta',
        attributes: {
          name: `twitter:label${i + 1}`,
          content: label.name,
        },
      })
      filteredMetaTags.push({
        tagName: 'meta',
        attributes: {
          name: `twitter:data${i + 1}`,
          content: label.value,
        },
      })
    })

    return filteredMetaTags
  }, [_seoMetaTags, labels])

  const linkTags = useMemo(() => {
    return _seoMetaTags.filter(tag => tag.tagName === 'link')
  }, [_seoMetaTags])

  const { locale } = useContext(languageContext)

  return (
    <Helmet htmlAttributes={{ lang: locale }}>
      {titleText && <title>{titleText}</title>}
      {linkTags.map(link => (
        <link key={link.attributes.href} {...link.attributes} />
      ))}
      {metaTags
        .filter(meta => 'attributes' in meta)
        .map(meta => (
          <meta
            key={'content' in meta.attributes ? meta.attributes.content : meta.attributes.hreflang}
            {...meta.attributes}
          />
        ))}
      {noIndex && <meta name="robots" content="noindex" />}
    </Helmet>
  )
}
